import React from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { FormValidationSummary, useFormValidation } from "../common/FormValidationSummary";
import type { FormStepProps } from "../../types/application";
import "../../assets/gcweb-theme.css";
import "../../assets/gcweb-font-fix.css";
import { isMobile } from "react-device-detect";
import { motion } from "framer-motion";

// Component for showing extracted data indicator
const ExtractedFieldIndicator: React.FC<{ isExtracted: boolean; t: any }> = ({
  isExtracted,
  t,
}) => {
  if (!isExtracted) return null;
  return (
    <span className="text-success small">
      <span
        className="glyphicon glyphicon-ok mrgn-rght-sm"
        aria-hidden="true"
      ></span>
      {t.prioritiesExtracted}
    </span>
  );
};

const PrioritiesEntitlementsStep: React.FC<FormStepProps> = ({
  data,
  onUpdate,
  onNext,
  onPrevious,
}) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const validation = useFormValidation({ t });
  const handleEnhancedDataChange = (field: string, value: string) => {
    const enhancedData = { ...data.enhancedData, [field]: value };
    onUpdate({ enhancedData });
  };

  const handleSubmit = (e: React.FormEvent) => {
    validation.handleSubmit(e, onNext);
  };

  const enhancedData = data.enhancedData || {};
  const extractedFields = data.extractionMetadata?.extractedFields || [];
  const isFieldExtracted = (fieldName: string) =>
    extractedFields.includes(fieldName);

  return (
    <motion.form
      ref={validation.formRef}
      className="wb-frmvld space-y-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onSubmit={handleSubmit}
      onChangeCapture={validation.handleChangeCapture}
      noValidate
    >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Information Box */}
        <section className="alert alert-info">
          <h3 className="h5">
            <strong>{t.prioritiesInfoBoxTitle}</strong>
          </h3>
          <p>{t.prioritiesInfoBoxText}</p>
        </section>

        {/* Priority Entitlement Status */}
        <div className="space-y-4">
          
            <fieldset className="gc-chckbxrdio">
              <legend
                className="block mb-2 text-gray-900"
                style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
              >
                <strong className="required text-red-700 mrgn-rght-sm">*</strong>
                <span className="field-name">6. {t.prioritiesLabel}</span>
                <strong className="required text-red-700"> {t.required}</strong>
              </legend>
              <p className="text-lg text-gray-700 mrgn-bttm-lg">
                {t.prioritiesLearnMore}{" "}
                <a
                  href={t.priorityEntitlementUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                  aria-label={`${t.prioritiesLearnMoreLink} (${t.opensInNewWindow})`}
                >
                  {t.prioritiesLearnMoreLink}
                </a>
              </p>
              <ul className="list-unstyled lst-spcd-2">
                <li className="radio">
                  <input
                    type="radio"
                    name="priorityEntitlementStatus"
                    id="priority-yes"
                    value="Yes"
                    checked={enhancedData.priorityEntitlementStatus === "Yes"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "priorityEntitlementStatus",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="priority-yes">{t.prioritiesYes}</label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="priorityEntitlementStatus"
                    id="priority-no"
                    value="No"
                    checked={enhancedData.priorityEntitlementStatus === "No"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "priorityEntitlementStatus",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="priority-no">{t.prioritiesNo}</label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("priorityEntitlementStatus")}
              t={t}
            />
        </div>

        {/* Navigation */}
        {isMobile ? (
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onPrevious}
              className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.previousButton}
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
            >
              {t.nextEmploymentButton}
            </button>
          </div>
        ) : (
          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.previousButton}
            </button>
            <button
              type="submit"
              className="px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
            >
              {t.nextEmploymentButton}
            </button>
          </div>
        )}
    </motion.form>
  );
};

export default PrioritiesEntitlementsStep;
